import React, { useState, useEffect } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertCircle,
  Building2,
  Layers,
  ExternalLink
} from 'lucide-react'
import { organizationService, FeatureAdoptionOrg, FeatureAdoptionLimit } from '../../services/organizationService'
import OrganizationTabbedDrawer from './OrganizationTabbedDrawer'

interface Feature {
  id: string
  name: string
  displayName: string
  description: string | null
  isActive: boolean
}

const formatLimitValue = (value: number): string => {
  if (value === -1) return 'Unlimited'
  return value.toLocaleString()
}

const formatCurrency = (value: number): string => {
  if (value === 0) return '—'
  return `$${value.toFixed(2)}/mo`
}

const LimitsTable: React.FC<{ limits: FeatureAdoptionLimit[] }> = ({ limits }) => {
  if (limits.length === 0) {
    return <p className="text-xs text-gray-400 italic px-4 py-2">No limits configured</p>
  }
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-gray-100">
          <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-1.5">Limit</th>
          <th className="text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-1.5">Value</th>
          <th className="text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-1.5">Tier Baseline</th>
          <th className="text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-1.5">Add'l Cost</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {limits.map((limit) => (
          <tr key={limit.limitName} className="hover:bg-gray-50">
            <td className="px-4 py-1.5">
              <span className="font-medium text-gray-700">{limit.limitDisplayName}</span>
              <span className="ml-2 text-[10px] text-gray-400 font-mono">{limit.limitName}</span>
            </td>
            <td className="px-4 py-1.5 text-right font-medium text-gray-900">
              {formatLimitValue(limit.limitValue)}
            </td>
            <td className="px-4 py-1.5 text-right text-gray-500">
              {formatLimitValue(limit.tierBaselineValue)}
            </td>
            <td className="px-4 py-1.5 text-right text-gray-500">
              {formatCurrency(limit.additionalMonthlyCost)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const OrgRow: React.FC<{
  org: FeatureAdoptionOrg
  onOpenDrawer: (orgId: string) => void
}> = ({ org, onOpenDrawer }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      <tr
        className="hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-1.5">
            {expanded
              ? <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              : <ChevronRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            }
            <span className="text-xs font-medium text-gray-900">{org.organizationName}</span>
          </div>
        </td>
        <td className="px-3 py-2.5">
          <span className="text-xs text-gray-600">{org.subscriptionTierName ?? '—'}</span>
        </td>
        <td className="px-3 py-2.5">
          {org.isTierDefault ? (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
              Tier Default
            </span>
          ) : (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
              Override
            </span>
          )}
        </td>
        <td className="px-3 py-2.5 text-right">
          <span className="text-xs text-gray-500">
            {formatCurrency(org.additionalMonthlyCost)}
          </span>
        </td>
        <td className="px-3 py-2.5 text-right">
          <span className="text-xs text-gray-500">
            {new Date(org.enabledSince).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </td>
        <td className="px-3 py-2.5 text-center">
          <button
            onClick={(e) => { e.stopPropagation(); onOpenDrawer(org.organizationId) }}
            className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            title="Open organization"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="bg-gray-50 border-b border-gray-200">
            <div className="py-2">
              <LimitsTable limits={org.limits} />
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

const FeatureAdoptionView: React.FC = () => {
  const [features, setFeatures] = useState<Feature[]>([])
  const [featuresLoading, setFeaturesLoading] = useState(true)
  const [selectedFeature, setSelectedFeature] = useState<string>('')

  const [adoptionData, setAdoptionData] = useState<{
    featureName: string
    featureDisplayName: string | null
    totalOrganizations: number
    organizations: FeatureAdoptionOrg[]
  } | null>(null)
  const [adoptionLoading, setAdoptionLoading] = useState(false)
  const [adoptionError, setAdoptionError] = useState<string | null>(null)

  const [drawerOrg, setDrawerOrg] = useState<any | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    organizationService.getAvailableFeatures().then((res) => {
      if (res.success) {
        setFeatures((res.data as any) ?? [])
      }
    }).finally(() => setFeaturesLoading(false))
  }, [])

  useEffect(() => {
    if (!selectedFeature) { setAdoptionData(null); return }
    setAdoptionLoading(true)
    setAdoptionError(null)
    organizationService.getFeatureAdoption(selectedFeature).then((res) => {
      if (res.success) {
        setAdoptionData(res.data)
      } else {
        setAdoptionError('Failed to load feature adoption data')
      }
    }).catch(() => {
      setAdoptionError('Failed to load feature adoption data')
    }).finally(() => setAdoptionLoading(false))
  }, [selectedFeature])

  const handleOpenDrawer = async (orgId: string) => {
    try {
      const res = await organizationService.getOrganization(orgId)
      setDrawerOrg(res.data ?? null)
    } catch {
      setDrawerOrg(null)
    }
    setDrawerOpen(true)
  }

  return (
    <div className="space-y-6">
      {/* Feature selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-violet-100 rounded-lg">
            <Layers className="h-5 w-5 text-violet-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Feature Adoption Report</h2>
            <p className="text-xs text-gray-500">Select a feature to see which organizations have it enabled along with their current limits</p>
          </div>
        </div>

        <div className="max-w-sm">
          <label className="block text-xs font-medium text-gray-700 mb-1">Feature</label>
          {featuresLoading ? (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading features...
            </div>
          ) : (
            <select
              value={selectedFeature}
              onChange={(e) => setSelectedFeature(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">— Select a feature —</option>
              {features.map((f) => (
                <option key={f.name} value={f.name}>
                  {f.displayName}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Results */}
      {selectedFeature && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {adoptionLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
            </div>
          ) : adoptionError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center mb-3">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <p className="text-sm font-semibold text-gray-900 mb-1">Failed to load data</p>
              <p className="text-xs text-gray-500">{adoptionError}</p>
            </div>
          ) : adoptionData ? (
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-800">
                    {adoptionData.featureDisplayName ?? adoptionData.featureName}
                  </span>
                </div>
                <span className="text-xs text-gray-500">
                  {adoptionData.totalOrganizations} organization{adoptionData.totalOrganizations !== 1 ? 's' : ''} enabled
                </span>
              </div>

              {adoptionData.organizations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">No organizations</p>
                  <p className="text-xs text-gray-500">No organizations currently have this feature enabled</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Organization</th>
                        <th className="text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Tier</th>
                        <th className="text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Source</th>
                        <th className="text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Add'l Cost</th>
                        <th className="text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Enabled Since</th>
                        <th className="text-center text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {adoptionData.organizations.map((org) => (
                        <OrgRow key={org.organizationId} org={org} onOpenDrawer={handleOpenDrawer} />
                      ))}
                    </tbody>
                  </table>
                  <p className="text-[10px] text-gray-400 px-4 py-2 border-t border-gray-100">
                    Click a row to expand and view organization limits
                  </p>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* Organization drawer */}
      <OrganizationTabbedDrawer
        isOpen={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDrawerOrg(null) }}
        organization={drawerOrg}
        isCreating={false}
        onSaved={() => {}}
      />
    </div>
  )
}

export default FeatureAdoptionView
